// TelcoTrack — Leaflet náhled vrstev při importu geometrie (mapování CAD → typ objektu).

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as L from 'leaflet';
import type { ResolvedImportSourceCrs } from '@core/shared-bridge';
import { environment } from '../../../../environments/environment';
import { importLayerPreviewColor, type ImportPreviewFeatureCollection } from '../../utils/import-geometry-preview.util';
import {
  ImportLayerMapOverlayHost,
  type ImportOverlayLayerMeta,
} from '../../utils/import-layer-map-overlay.host';

export type ImportPreviewLayerMeta = ImportOverlayLayerMeta;

@Component({
  selector: 'app-import-layer-map-preview',
  templateUrl: './import-layer-map-preview.component.html',
  styleUrls: ['./import-layer-map-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportLayerMapPreviewComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLElement>;

  @Input() geoJson: ImportPreviewFeatureCollection | null = null;
  @Input() layers: ImportPreviewLayerMeta[] = [];
  @Input() selectedSourceCrs: ResolvedImportSourceCrs | 'auto' = 'auto';
  @Input() detectedSourceCrs: ResolvedImportSourceCrs | null = null;
  /** Highlighted layer name (from list hover/click). */
  @Input() highlightLayerName: string | null = null;
  /** When set, fit map to this layer once (parent clears after). */
  @Input() fitLayerName: string | null = null;

  @Output() layerActivated = new EventEmitter<string>();
  @Output() fitLayerHandled = new EventEmitter<void>();

  hasGeometry = false;
  truncated = false;
  unavailableReason: string | null = null;
  isBuildingPreview = false;

  private map: L.Map | null = null;
  private viewReady = false;
  private resizeObserver: ResizeObserver | null = null;
  private readonly overlay = new ImportLayerMapOverlayHost();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.initMap();
    // First paint after map exists — always fit once WGS84 overlays are ready.
    void this.renderIfReady(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['geoJson'] ||
      changes['layers'] ||
      changes['selectedSourceCrs'] ||
      changes['detectedSourceCrs'] ||
      changes['highlightLayerName']
    ) {
      const forceFit = !!(
        changes['geoJson'] ||
        changes['selectedSourceCrs'] ||
        changes['detectedSourceCrs']
      );
      void this.renderIfReady(forceFit);
    }
    if (changes['fitLayerName'] && this.fitLayerName) {
      this.overlay.fitToLayer(this.fitLayerName);
      this.fitLayerHandled.emit();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.overlay.detach();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  layerColor(layerName: string): string {
    const idx = this.layers.findIndex((l) => l.layerName === layerName);
    return importLayerPreviewColor(idx >= 0 ? idx : 0);
  }

  private initMap(): void {
    const el = this.mapContainer?.nativeElement;
    if (!el || this.map) return;
    this.map = L.map(el, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
      dragging: true,
      doubleClickZoom: true,
      boxZoom: false,
      keyboard: false,
    });
    L.tileLayer(environment.maps.tileUrl, {
      maxZoom: 19,
      attribution: environment.maps.attribution,
    }).addTo(this.map);
    this.map.setView(
      [environment.maps.defaultCenter[0], environment.maps.defaultCenter[1]],
      7,
    );
    this.overlay.attach(this.map, {
      onLayerActivated: (name) => this.layerActivated.emit(name),
    });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.map?.invalidateSize({ animate: false });
      });
      this.resizeObserver.observe(el);
    }
  }

  private async renderIfReady(forceFit = false): Promise<void> {
    if (!this.viewReady || !this.map) return;
    this.overlay.setInputs({
      geoJson: this.geoJson,
      layers: this.layers,
      selectedSourceCrs: this.selectedSourceCrs,
      detectedSourceCrs: this.detectedSourceCrs,
      highlightLayerName: this.highlightLayerName,
    });
    await this.overlay.render({ forceFit });
    this.hasGeometry = this.overlay.hasGeometry;
    this.truncated = this.overlay.truncated;
    this.unavailableReason = this.overlay.unavailableReason;
    this.isBuildingPreview = this.overlay.isBuildingPreview;
    this.cdr.markForCheck();
  }
}
